/**
 * Hook: useScheduledMeetingsMutations
 *
 * Provides functions to modify (create, update, delete) scheduled meetings.
 * This is the "writing" part of the context interface.
 *
 * It calls the master `useScheduledMeetings` hook internally and exposes
 * only the mutation functions.
 */
import { useCallback } from 'react';
import type { CreateMeetingParams, UpdateMeetingParams } from '../types';
import useScheduledMeetings from './useScheduledMeetings';

const useScheduledMeetingsMutations = () => {
  const { createMeeting, updateMeeting, deleteMeeting, mutations } = useScheduledMeetings();

  /**
   * Creates a new scheduled meeting and updates the cache.
   */
  const create = useCallback(
    async (params: CreateMeetingParams) => {
      return createMeeting(params);
    },
    [createMeeting],
  );

  /**
   * Updates an existing scheduled meeting with optimistic updates.
   */
  const update = useCallback(
    async (params: UpdateMeetingParams) => {
      return updateMeeting(params);
    },
    [updateMeeting],
  );

  /**
   * Deletes a scheduled meeting with optimistic updates.
   */
  const remove = useCallback(
    async (room: string) => {
      await deleteMeeting(room);
    },
    [deleteMeeting],
  );

  return {
    create,
    update,
    remove,
    isCreating: mutations.create.isPending,
    isUpdating: mutations.update.isPending,
    deletingRoom: mutations.delete.isPending ? mutations.delete.pendingRoom : null,
  };
};

export default useScheduledMeetingsMutations;
